import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Headphones, Megaphone } from "lucide-react";
import { getHome, getStats } from "@/lib/api";
import { TYPES, NAV_ORDER, linkTo, getTitle, getImage, getBlurb } from "@/config";
import { EntryCard, ListRow } from "@/components/Cards";
import { AtlasTeaser } from "@/components/AtlasTeaser";

export default function Home() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState({});

  useEffect(() => {
    getHome().then(setData).catch(() => setData({}));
    getStats().then(setStats).catch(() => {});
  }, []);

  if (!data) {
    return <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-32 eyebrow">Loading the archive…</div>;
  }

  const fc = data.featured_case;

  return (
    <div>
      {/* HERO */}
      <section className="relative border-b border-line">
        <div className="absolute inset-0">
          <img
            src={getImage(fc) || "https://images.unsplash.com/photo-1606819717115-9159c900370b?w=1600&q=85"}
            alt=""
            className="h-full w-full object-cover opacity-30 grayscale-[30%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10 pt-24 pb-20 lg:pt-36 lg:pb-28">
          <div className="max-w-4xl fade-up">
            <div className="eyebrow mb-6 text-gold">The Library of Art Crime, Law & Controversy</div>
            <h1 className="font-serif text-5xl leading-[0.95] tracking-tight text-parchment sm:text-6xl lg:text-7xl">
              The definitive record of art crime, law, and controversy.
            </h1>
            <p className="mt-8 max-w-2xl font-serif text-xl leading-relaxed text-faint sm:text-2xl">
              From antiquity to the present — an interconnected archive of the thefts, forgeries,
              investigations, artists, institutions, and legal battles that have shaped the history of art.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/cases"
                data-testid="hero-browse-cases"
                className="inline-flex items-center gap-2 bg-gold px-7 py-3.5 text-sm font-semibold uppercase tracking-widest text-ink hover:bg-gold-hover transition-colors"
              >
                Browse the Cases
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 border border-line px-7 py-3.5 eyebrow text-parchment hover:border-gold/60 hover:text-gold transition-colors"
              >
                About the Project
              </Link>
              <Link
                to="/about#submit-tip"
                data-testid="hero-submit-tip"
                className="inline-flex items-center gap-2 border border-gold/50 px-7 py-3.5 eyebrow text-gold hover:bg-gold hover:text-ink transition-colors"
              >
                <Megaphone size={15} /> Submit a Tip?
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-b border-line bg-surface">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 divide-x divide-line">
            {NAV_ORDER.map((k) => (
              <Link key={k} to={`/${TYPES[k].segment}`} data-testid={`stat-${k}`}
                className="group px-4 py-6 hover:bg-elevated transition-colors">
                <div className="font-serif text-4xl text-gold">{stats[k] ?? "—"}</div>
                <div className="eyebrow mt-1 group-hover:text-parchment transition-colors">{TYPES[k].label}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED CASE + LATEST NEWS (Tetris) */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24">
        <div className="grid gap-8 lg:grid-cols-12">
          {fc && (
            <Link
              to={linkTo("cases", fc.slug)}
              data-testid="featured-case"
              className="group relative col-span-12 lg:col-span-8 overflow-hidden border border-line bg-surface"
            >
              <div className="aspect-[16/10] overflow-hidden bg-elevated">
                {getImage(fc) && <img src={getImage(fc)} alt={getTitle(fc)} className="card-img h-full w-full object-cover grayscale-[15%]" />}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
              <div className="absolute bottom-0 p-8 lg:p-12">
                <div className="eyebrow mb-3 text-gold">Featured Investigation · {fc.category}</div>
                <h2 className="font-serif text-4xl leading-tight text-parchment lg:text-5xl">{getTitle(fc)}</h2>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-parchment/70">{getBlurb(fc)}</p>
                <span className="mt-5 inline-flex items-center gap-1 eyebrow text-gold">
                  Read the case <ArrowUpRight size={14} />
                </span>
              </div>
            </Link>
          )}

          <div className="col-span-12 lg:col-span-4">
            <div className="flex items-baseline justify-between border-b border-gold/30 pb-3">
              <h3 className="font-serif text-2xl text-parchment">Latest News</h3>
              <Link to="/news" className="eyebrow text-gold hover:text-gold-hover">All →</Link>
            </div>
            <div className="mt-2">
              {(data.news || []).map((n, i) => (
                <ListRow key={n.id} item={n} ctype="news" index={i} testid={`home-news-${n.slug}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CASES GRID */}
      <SectionGrid title="From the Casebook" ctype="cases" items={data.cases} to="/cases" />

      {/* ATLAS TEASER */}
      <AtlasTeaser />

      {/* PEOPLE + PODCAST */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 border-t border-line">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <div className="flex items-baseline justify-between border-b border-gold/30 pb-3">
              <h3 className="font-serif text-3xl text-parchment">Figures of Note</h3>
              <Link to="/people" className="eyebrow text-gold">All People →</Link>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-6">
              {(data.people || []).slice(0, 4).map((p) => (
                <Link key={p.id} to={linkTo("people", p.slug)} data-testid={`home-person-${p.slug}`}
                  className="group">
                  <div className="aspect-square overflow-hidden border border-line bg-elevated">
                    {getImage(p) && <img src={getImage(p)} alt={p.name} className="card-img h-full w-full object-cover grayscale" />}
                  </div>
                  <div className="eyebrow mt-3 text-gold/70">{(p.roles || []).join(" · ")}</div>
                  <div className="font-serif text-xl text-parchment group-hover:text-gold transition-colors">{p.name}</div>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between border-b border-gold/30 pb-3">
              <h3 className="font-serif text-3xl text-parchment">The Podcast</h3>
              <Link to="/podcast" className="eyebrow text-gold">All Episodes →</Link>
            </div>
            <div className="mt-4">
              {(data.episodes || []).map((ep) => (
                <Link key={ep.id} to={linkTo("episodes", ep.slug)} data-testid={`home-episode-${ep.slug}`}
                  className="group flex items-center gap-4 border-b border-line py-4">
                  <div className="h-16 w-16 shrink-0 overflow-hidden border border-line bg-elevated">
                    {getImage(ep) && <img src={getImage(ep)} alt="" className="h-full w-full object-cover grayscale-[20%]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="eyebrow text-faint">Ep. {ep.episode_number} · {ep.duration}</div>
                    <div className="font-serif text-xl text-parchment group-hover:text-gold transition-colors truncate">{ep.title}</div>
                  </div>
                  <Headphones size={18} className="text-faint group-hover:text-gold shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ESSAYS */}
      <SectionGrid title="Essays & Criticism" ctype="essays" items={data.essays} to="/essays" cols={3} />

      {/* MUSEUM MISHAPS */}
      <SectionGrid title="Museum Mishaps" ctype="mishaps" items={data.mishaps} to="/museum-mishaps" cols={3} />
    </div>
  );
}

function SectionGrid({ title, ctype, items = [], to, cols = 3 }) {
  if (!items.length) return null;
  return (
    <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 border-t border-line">
      <div className="flex items-baseline justify-between border-b border-gold/30 pb-4">
        <h3 className="font-serif text-3xl text-parchment lg:text-4xl">{title}</h3>
        <Link to={to} className="eyebrow text-gold hover:text-gold-hover">View all →</Link>
      </div>
      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.slice(0, 6).map((it) => (
          <EntryCard key={it.id} item={it} ctype={ctype} testid={`home-${ctype}-${it.slug}`} />
        ))}
      </div>
    </section>
  );
}
